import { useEffect, useMemo, useState } from 'react';
import { Card, Title, Text, Button, TextInput, Select, SelectItem } from '@tremor/react';
import { fetchInvEditRecord, postInvEdit } from '../api/invEdit';
import type { InvEditRecord, User } from '../api/types';
import { t } from '../lib/i18n';

/**
 * JCD-56 — the inventory edit panel (Phase 1).
 *
 * Field groups mirror internal/invedit/fields.go's catalog order, which is
 * also the order the write verb applies them in. `kind` drives the input:
 * 'int' and 'dec' are numeric, 'str' is text, 'bool' is a Sí/No select
 * because the wire carries 0/1 — the daemons' hand-rolled JSON parser has no
 * true/false.
 *
 * On-hand and every history field are deliberately absent: they are not in
 * the catalog, so the server would reject them anyway. On-hand is shown
 * read-only with a pointer to Ajustar existencia.
 */
type Kind = 'int' | 'dec' | 'str' | 'bool';
interface FieldDef {
  wire: string;
  kind: Kind;
}

type GroupKey = 'stocking' | 'packaging' | 'flags' | 'operational' | 'prices' | 'costs';

const GROUPS: { key: GroupKey; fields: FieldDef[] }[] = [
  {
    key: 'stocking',
    fields: [
      { wire: 'min1', kind: 'int' },
      { wire: 'max1', kind: 'int' },
      { wire: 'min2', kind: 'int' },
      { wire: 'max2', kind: 'int' },
      { wire: 'bin_min', kind: 'int' },
      { wire: 'bin_max', kind: 'int' },
      { wire: 'bin', kind: 'str' },
      { wire: 'altbin', kind: 'str' },
      { wire: 'class', kind: 'str' },
      { wire: 'taxst', kind: 'str' },
    ],
  },
  {
    key: 'packaging',
    fields: [
      { wire: 'pack', kind: 'int' },
      { wire: 'ordpack', kind: 'int' },
      { wire: 'u_of_ms', kind: 'str' },
      { wire: 'sellqty', kind: 'int' },
      { wire: 'break_pack', kind: 'bool' },
    ],
  },
  {
    key: 'flags',
    fields: [
      { wire: 'ckstk', kind: 'bool' },
      { wire: 'ord_flag', kind: 'bool' },
      { wire: 'manupd_flag', kind: 'bool' },
      { wire: 'master_inst', kind: 'bool' },
    ],
  },
  {
    key: 'operational',
    fields: [
      { wire: 'bkoqty', kind: 'int' },
      { wire: 'infrght1', kind: 'dec' },
      { wire: 'infrght2', kind: 'dec' },
    ],
  },
  {
    key: 'prices',
    fields: [
      { wire: 'plist', kind: 'dec' },
      { wire: 'price1', kind: 'dec' },
      { wire: 'price2', kind: 'dec' },
      { wire: 'price3', kind: 'dec' },
      { wire: 'pcore', kind: 'dec' },
    ],
  },
  {
    key: 'costs',
    fields: [
      { wire: 'cost1', kind: 'dec' },
      { wire: 'cost2', kind: 'dec' },
      { wire: 'costave', kind: 'dec' },
      { wire: 'pccost', kind: 'dec' },
    ],
  },
];

function label(wire: string): string {
  const m = t.part.edit.f as Record<string, string>;
  return m[wire] ?? wire;
}

interface Props {
  sid: number;
  line: string;
  part: string;
  user: User;
  writesEnabled: boolean;
  onSaved: () => void;
}

export default function PartEditPanel({ sid, line, part, user, writesEnabled, onSaved }: Props) {
  // Same gate shape as Discrepancias.tsx: the write permission AND the
  // server's own writes_enabled. Both must hold or the panel does not exist.
  const canEdit =
    writesEnabled &&
    (user.role === 'owner' || (user.permissions ?? []).includes('inventory:edit'));

  const [rec, setRec] = useState<InvEditRecord | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!canEdit) return;
    let alive = true;
    fetchInvEditRecord(sid, line, part)
      .then((r) => {
        if (!alive) return;
        setRec(r);
        const d: Record<string, string> = {};
        Object.entries(r.fields).forEach(([k, v]) => {
          d[k] = String(v);
        });
        setDraft(d);
      })
      .catch((e) => {
        if (alive) setErr((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [sid, line, part, canEdit]);

  // Only fields whose value actually differs from the loaded record are sent:
  // the server writes exactly the keys it is given, and an unchanged field
  // would otherwise cost an audit row for nothing.
  const changes = useMemo(() => {
    const out: Record<string, number | string> = {};
    if (!rec) return out;
    for (const g of GROUPS) {
      for (const f of g.fields) {
        if (!(f.wire in rec.fields)) continue;
        const cur = String(rec.fields[f.wire]);
        const next = draft[f.wire];
        if (next === undefined || next === cur) continue;
        out[f.wire] = f.kind === 'str' ? next : Number(next);
      }
    }
    return out;
  }, [rec, draft]);

  if (!canEdit) return null;
  if (err && !rec) {
    return (
      <Card className="mt-4">
        <Text color="red">{err}</Text>
      </Card>
    );
  }
  if (!rec) {
    return (
      <Card className="mt-4">
        <Text>{t.part.edit.loading}</Text>
      </Card>
    );
  }

  const curPack = Number(rec.fields.pack ?? 1) || 1;

  function validate(): string | null {
    if (!rec) return null;
    for (const [k, v] of Object.entries(changes)) {
      if (typeof v !== 'number') continue;
      if (Number.isNaN(v)) return `${label(k)}: ${t.part.edit.errPositive}`;
      if (v < 0) return `${label(k)}: ${t.part.edit.errPositive}`;
      if ((k === 'pack' || k === 'sellqty' || k === 'ordpack') && v < 1) {
        return `${label(k)}: ${t.part.edit.errAtLeastOne}`;
      }
    }
    // Mirrors invedit.Validate: ordpack divides the INCOMING pack when pack
    // is changing in the same edit, else the record's current pack.
    const pack = 'pack' in changes ? Number(changes.pack) : curPack;
    const ordpack = 'ordpack' in changes ? Number(changes.ordpack) : Number(rec.fields.ordpack ?? 1);
    if (pack >= 1 && ordpack >= 1 && ordpack % pack !== 0) return t.part.edit.errOrdpack;
    return null;
  }

  function onSave() {
    setOkMsg(null);
    setErr(null);
    if (Object.keys(changes).length === 0) {
      setErr(t.part.edit.noChanges);
      return;
    }
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setConfirming(true);
  }

  async function onConfirm() {
    setSaving(true);
    setErr(null);
    try {
      await postInvEdit(sid, line, part, changes, curPack);
      setConfirming(false);
      setOkMsg(t.part.edit.saved);
      const fresh = await fetchInvEditRecord(sid, line, part);
      setRec(fresh);
      const d: Record<string, string> = {};
      Object.entries(fresh.fields).forEach(([k, v]) => {
        d[k] = String(v);
      });
      setDraft(d);
      onSaved();
    } catch (e) {
      // Keep the form as the user left it: a rejected edit is usually one
      // bad field, and clearing their work would be hostile.
      setErr((e as Error).message);
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="mt-4">
      <Title>{t.part.edit.title}</Title>
      <Text className="mt-1">
        {t.part.edit.readOnly.instk}: {rec.read_only.instk} · {t.part.edit.onHandNote}
      </Text>
      <Text>{t.part.edit.historyNote}</Text>

      {GROUPS.map((g) => {
        // A field is offered only when the record carries it: absent means
        // either this site's schema lacks it (schema_missing) or the user
        // lacks cost:visible, and in both cases the write would refuse it.
        const visible = g.fields.filter(
          (f) => f.wire in rec.fields && !rec.schema_missing.includes(f.wire),
        );
        if (visible.length === 0) return null;
        return (
          <div key={g.key} className="mt-4">
            <Text className="font-medium">{t.part.edit.groups[g.key]}</Text>
            <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
              {visible.map((f) => (
                <div key={f.wire}>
                  <label htmlFor={`invedit-${f.wire}`} className="block text-sm">
                    {label(f.wire)}
                  </label>
                  {f.kind === 'bool' ? (
                    <Select
                      id={`invedit-${f.wire}`}
                      value={draft[f.wire] ?? '0'}
                      onValueChange={(v) => setDraft((d) => ({ ...d, [f.wire]: v }))}
                    >
                      <SelectItem value="1">Sí</SelectItem>
                      <SelectItem value="0">No</SelectItem>
                    </Select>
                  ) : (
                    <TextInput
                      id={`invedit-${f.wire}`}
                      value={draft[f.wire] ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [f.wire]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {err && (
        <Text color="red" className="mt-3">
          {err}
        </Text>
      )}
      {okMsg && (
        <Text color="green" className="mt-3">
          {okMsg}
        </Text>
      )}

      <Button className="mt-4" onClick={onSave} disabled={saving}>
        {t.part.edit.save}
      </Button>

      {confirming && (
        <div className="mt-4 border-t pt-3">
          <Text className="font-medium">{t.part.edit.confirmTitle}</Text>
          <ul className="mt-2">
            {Object.entries(changes).map(([k, v]) => (
              <li key={k}>
                {label(k)}: {String(rec.fields[k])} → {String(v)}
              </li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2">
            <Button onClick={onConfirm} disabled={saving}>
              {t.part.edit.confirm}
            </Button>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={saving}>
              {t.part.edit.cancel}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
